import { useEffect } from "react";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "./app/store";
import type {
  AppBootstrapOutletContext,
  RootOutletContext,
} from "./shared/outletContextsTypes";
import { hydrateResourceBookmarks } from "./stores/bookmarks/resourceBookmarksSlice";
import { hydrateMentorBookmarks } from "./stores/bookmarks/mentorBookmarksSlice";
import { hydrateMentorRecommendations } from "./stores/recommendations/mentorRecommendationsSlice";
import { AccessCodeEntry } from "./components/Gates/AccessCodeEntry";
import { useAccessGate } from "./hooks/use-access-gate";
import {
  setAccessCode,
  startNewSession,
} from "./stores/sessionData/sessionDataSlice";
import { LoadingGate } from "./components/Gates/LoadingGate";
import { hydrateUser } from "./stores/user/userSlice";
import { useUserObjectExists } from "./hooks/use-user-object-exists";
import { sendUserLoginEvent } from "./utils/event-helpers";

const PILOT_EXPIRATION_TIME = Number(
  import.meta.env.VITE_PILOT_EXPIRATION_TIME,
);
const REDIRECT_URL = import.meta.env.VITE_PRE_SURVEY_URL;
const SITE_ACCESS_EXPIRATION_KEY = "dateOfExpiration";

export default function AppBootstrap() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { gradePosted, submittingGrade, isLtikSession, canvasId } =
    useOutletContext<RootOutletContext>();

  const { accessCode, userId, sessionId } = useAppSelector(
    (state) => state.session,
  );

  const handleSetAccessCode = (code: string | null) => {
    dispatch(setAccessCode(code));
  };

  const userData = useAppSelector((state) => state.user.data);

  const urlPathname = useLocation().pathname;

  const expirationTimeString = localStorage.getItem(SITE_ACCESS_EXPIRATION_KEY);

  const isPilotParticipant = userId?.startsWith("PathwaysPilot") ?? false;

  const isResearchParticipant = userData?.status.isResearchParticipant ?? false;

  const completedPreSurvey = userData?.status.isPreTestSurveyComplete ?? false;

  const completedPostSurvey =
    userData?.status.isPostTestSurveyComplete ?? false;

  const { gateState, gateError, submitCode } = useAccessGate({
    accessCode,
    setAccessCode: handleSetAccessCode,
  });

  const { userBootstrapState } = useUserObjectExists(
    userId ?? null,
    gateState === "unlocked",
  );

  const outletContext: AppBootstrapOutletContext = {
    canvasId,
    submittingGrade,
    gradePosted,
    isLtikSession,
    isResearchParticipant,
    isPilotParticipant,
    completedPreSurvey,
    completedPostSurvey,
  };

  useEffect(() => {
    if (userId?.startsWith("Pathways")) return;

    window.location.replace(
      canvasId ? REDIRECT_URL + "?canvasid=" + canvasId : REDIRECT_URL,
    );
  }, [userId, canvasId]);

  useEffect(() => {
    if (isPilotParticipant && !expirationTimeString) {
      const today = new Date();
      const expirationTime = new Date(today.getTime() + PILOT_EXPIRATION_TIME);

      localStorage.setItem(
        SITE_ACCESS_EXPIRATION_KEY,
        expirationTime.toISOString(),
      );
    }
  }, [expirationTimeString, isPilotParticipant]);

  useEffect(() => {
    if (
      urlPathname !== "/pilotstudyfinish" &&
      isPilotParticipant &&
      expirationTimeString &&
      new Date().getTime() > new Date(expirationTimeString).getTime()
    ) {
      navigate("/pilotstudyfinish");
    }
  }, [expirationTimeString, isPilotParticipant, navigate, urlPathname]);

  useEffect(() => {
    if (!userId) return;
    if (sessionId) return;

    dispatch(startNewSession());
  }, [dispatch, userId, sessionId]);

  useEffect(() => {
    if (!userId) return;
    if (!userData) return;

    void sendUserLoginEvent(userId);
  }, [userData, userId]);

  useEffect(() => {
    if (!userId) return;
    if (!userData) return;

    if (!isPilotParticipant && !completedPreSurvey) {
      window.location.replace(
        canvasId ? REDIRECT_URL + "?canvasid=" + canvasId : REDIRECT_URL,
      );
    }
  }, [canvasId, completedPreSurvey, isPilotParticipant, userData, userId]);

  useEffect(() => {
    if (!userId) return;
    if (userBootstrapState !== "ready") return;

    void dispatch(hydrateUser(userId));
    void dispatch(hydrateResourceBookmarks(userId));
    void dispatch(hydrateMentorBookmarks(userId));
    void dispatch(hydrateMentorRecommendations(userId));
  }, [dispatch, userId, userBootstrapState]);

  if (!userId) {
    return (
      <LoadingGate
        title="Redirecting to login..."
        subtitle="We could not find your user information. Please wait while we send you to the login form."
      />
    );
  }

  if (gateState === "bootstrapping") {
    return (
      <LoadingGate
        title="Checking access..."
        subtitle="Please wait while we verify your access code."
        dataCy="access-code-bootstrapping"
      />
    );
  }

  if (gateState !== "unlocked") {
    return (
      <AccessCodeEntry
        onSubmit={submitCode}
        error={gateError}
        isSubmitting={gateState === "submitting"}
        dataCy="access-code-form"
      />
    );
  }

  if (userBootstrapState === "polling" || !userData) {
    return (
      <LoadingGate
        title="Preparing your account..."
        subtitle={
          <>
            Please wait while we finish signing you in. If it is taking too
            long, try{" "}
            <a
              href={
                canvasId ? REDIRECT_URL + "?canvasid=" + canvasId : REDIRECT_URL
              }
            >
              signing in again
            </a>
            .
          </>
        }
        dataCy="user-loading-gate"
      />
    );
  }

  return <Outlet context={outletContext} />;
}
